import { inject, Injectable } from "@angular/core";

import { RestService } from "./rest.service";
import { AuthenticationService } from "./authentication.service";

import { TsXmlCodelistEntries } from "../tsXml/tsXmlCodelistEntries.model";

import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root'
})
export class ActiveProductsService {
  private authServ = inject(AuthenticationService);
  private restServ = inject(RestService);

  private readonly accessibleProducts = Object.freeze({
    cs: ['AP1', 'RP1', 'PM1', 'FP1'],
    sk: ['AP2'],
    en: []
  });

  private readonly obj = {
    PILLOW_CZ: 'cs',
    PILLOW_SK: 'sk'
  };

  private uid: string;
  private products: string[];

  constructor(
  ) {
    this.uid = this.authServ.currentUserUidValue?.uid;
  }

  public async getActiveProducts(): Promise<string[]> {
    const usr = this.authServ.currentUserValue;
    if (this.authServ.currentUserUidValue?.uid !== this.uid) this.products = null;
    if (this.products) return this.products;
    const comp = usr?.party?.insuranceCompany;
    const lang = comp ? this.obj[comp] : environment.language;
    const products = [...this.accessibleProducts[lang]];
    const userType = usr.userType;
    if (userType === 3) {
      Object.keys(this.accessibleProducts).filter(it => it !== lang).forEach(lg => {
        products.push(...this.accessibleProducts[lg]);
      });
    }

    const output = [];
    for (const product of products) {
      if (userType < 2 || await this.isProductAccessible(product)) {
        const isSp = ['OSVC', 'PO'].includes(usr?.party?.partyType ?? '');
        const isPR = ['PM', 'RP'].includes(product.slice(0, 2));
        const isFleet = product === 'FP1';
        if (userType > 1 || ((!isSp || !isPR) && (!isFleet || isSp))) {
          output.push(product);
        }
      }
    }
    this.products = output;
    return this.products;
  }

  private async isProductAccessible(product: string): Promise<boolean> {
    if (!['AP1', 'AP2', 'RP1', 'PM1', 'FP1'].includes(product)) throw new Error(`isProductAccessible wrong input ${product}`);
    const usr = this.authServ.currentUserValue;
    let resp: TsXmlCodelistEntries;
    const isFleet = product === 'FP1';
    try {
      resp = await this.restServ.myParCodelistsEntriesByColumnsPost(
        '5225',
        [
          { code: 'PRODUKT', value: isFleet ? 'AP1' : product },
          { code: 'AGNT', value: usr.agentId1 }
        ],
        this.authServ.currentUserUidValue
      );
    } catch {
      return false;
    }
    if (resp.entry)
      resp.entry = resp.entry.filter(it => it.gclColumn.some(itm => itm.columnCode === 'AGNT' && itm.stringValue === usr.agentId1));
    let output = !!resp?.entry?.length;
    if (output) {
      const since = new Date(resp.entry[0].gclColumn.find(it => it.columnCode === 'PLOD').dateValue);
      const till = new Date(resp.entry[0].gclColumn.find(it => it.columnCode === 'PLDO').dateValue);
      const now = await this.restServ.currentTimeJBossGet(this.authServ.currentUserUidValue);
      if (since.getTime() > now.getTime() || till.getTime() < now.getTime())
        output = false;
    }

    if (isFleet) {
      if (!output) return false;
      const fleeted = resp.entry.some(it => it.gclColumn.find(it => it.columnCode === 'FLOTILA').booleanValue);
      if (usr.userType === 1) {
        if (['OSVC', 'PO'].includes(usr.party.partyType)) return false;
        try {
          const policies = await this.restServ.getFramePolicies(
            { partyId: usr.party.companyIdentification },
            this.authServ.currentUserUidValue
          );
          return !policies?.totalEntries;
        } catch {
          return false;
        }
      } else {
        return fleeted;
      }

    }

    return output; // TODO
  }

  public getLocation(product: string) {
    if (this.authServ.currentUserValue.userType < 3) return null;
    if (product === 'FP1') return 'cs';
    if (this.accessibleProducts.cs.includes(product) && this.hasATwin(product, 'cs')) return 'cs';
    if (this.accessibleProducts.sk.includes(product) && this.hasATwin(product, 'sk')) return 'sk';
    if (this.accessibleProducts.en.includes(product) && this.hasATwin(product, 'en')) return 'en';
    return null;
  }

  private hasATwin(product: string, lang: string): boolean {
    for (const lg of Object.keys(this.accessibleProducts).filter(it => it !== lang)) {
      if (this.accessibleProducts[lg].some((it: string) => it.startsWith(product.slice(0, -1)))) return true;
    }
    return false;
  }

  public getAllProducts(insuranceCompany: 'PILLOW_CZ' | 'PILLOW_SK') {
    return this.accessibleProducts[this.obj[insuranceCompany]];
  }
}

